
import * as React from 'react';
import { IEntry } from '../../utilities/fileSystem/FileEntries';
import { CancelError, isCancel, CancelSource } from '../../utilities/async/CancelError';
import { IItemCacheDispatch } from '../../store/actions/ItemCacheActions';
import { connectToItemCache, IItemCacheDispatchProp } from '../decorators/connectToItemCache';
import { IOnExecuteProps } from '../commandSet/CommandSet';
import { IDecoratedProps } from '../../utilities/components/Decorators';
import {
  IOnItemUploadedProps,
  IOnItemQueuedProps,
  IOnItemFailedProps,
  IOnEntryBatchCreatedProps,
  IOnEntryBatchUploadedProps,
  IOnEntryBatchFailedProps,
  uploadEntryBatch
} from '../../operations/uploadItems/uploadEntries';
import { uploadProgressAddon } from '../../operations/uploadItems/UploadProgressAddon';
import { IWithWizardInjectedProps } from '../decorators/withWizard';

export { IOnItemUploadedProps, IOnItemQueuedProps };

/**
 * @public
 */
export interface IOnUploadStartedProps {
  progressItemKey: string;
}

/**
 * @public
 */
export interface IOnUploadCompletedProps {
  progressItemKey: string;
}

/**
 * @public
 */
export interface IOnUploadFailedProps {
  progressItemKey: string;
  error: Error;
}

/**
 * @public
 */
export interface IFileSelectorProps {
  onExecuteProps?: IOnExecuteProps;
  onContinue: (entries: IEntry[]) => void;
  onCancel: () => void;
}

/**
 * Props for an Uploader, which are shared by UploadAction.
 * @public
 */
export interface IBaseUploaderCoreProps extends IWithWizardInjectedProps {
  /**
   * The target folder item key for the upload.
   */
  itemKey: string;
  /**
   * The key for the entry batch to upload.
   */
  entryBatchKey: string;
  /**
   * The dispatcher for the item cache store.
   */
  itemCacheDispatch: IItemCacheDispatch;
  /**
   * Optional callback which indicates when an upload batch has started.
   */
  onUploadStarted?: (props: IOnUploadStartedProps) => void;
  /**
   * Optional callback which indicates when an upload batch has completed.
   */
  onUploadCompleted?: (props: IOnUploadCompletedProps) => void;
  /**
   * Optional callback which indicates when an upload batch has failed.
   */
  onUploadFailed?: (props: IOnUploadFailedProps) => void;
  /**
   * Optional callback which indicates when a single item has been queued for upload.
   */
  onItemQueued?: (props: IOnItemQueuedProps) => void;
  /**
   * Optional callback which indicates when a single item has uploaded.
   */
  onItemUploaded?: (props: IOnItemUploadedProps) => void;
  /**
   * Optional callback which indicates when a single item has failed to upload.
   */
  onItemFailed?: (props: IOnItemFailedProps) => void;
}

/**
 * @internal
 */
export interface IUploaderCoreState {
  isStarted: boolean;
  isCanceled: boolean;
  isComplete: boolean;
  error: Error | undefined;
}

const UPLOADER_CORE_INITIAL_STATE: IUploaderCoreState = {
  isStarted: false,
  isCanceled: false,
  error: undefined,
  isComplete: false
};

/**
 * Component which manages the state machine for an Upload action.
 * @internal
 */
export class BaseUploaderCore extends React.PureComponent<IBaseUploaderCoreProps, IUploaderCoreState> {
  private _cancelSource: CancelSource;

  constructor(props: IBaseUploaderCoreProps) {
    super(props);

    this.state = UPLOADER_CORE_INITIAL_STATE;

    this._cancelSource = new CancelSource();
  }

  public render(): JSX.Element | null {
    return null;
  }

  public componentDidMount(): void {
    this._onStateChanged(UPLOADER_CORE_INITIAL_STATE);
  }

  public componentDidUpdate(previousProps: IBaseUploaderCoreProps, previousState: IUploaderCoreState): void {
    this._onStateChanged(previousState);
  }

  public componentWillUnmount(): void {
    this._cancelSource.cancel();
  }

  private _onStateChanged(previousState: IUploaderCoreState): void {
    const {
      isCanceled,
      isComplete
    } = this.state;

    if (!previousState.isComplete && isComplete) {
      this.props.resolve();
    } else if (!previousState.error && this.state.error) {
      this.props.reject(this.state.error);
    } else if (!previousState.isCanceled && isCanceled) {
      this.props.reject(new CancelError());
    } else if (!this.state.isStarted) {
      this._startUpload();
    }
  }

  private async _startUpload(): Promise<void> {
    const {
      itemCacheDispatch,
      entryBatchKey
    } = this.props;

    this.setState({
      isStarted: true
    });

    try {
      itemCacheDispatch(uploadProgressAddon);

      await itemCacheDispatch(uploadEntryBatch({
        parentItemKey: this.props.itemKey,
        sourceEntryKey: entryBatchKey,
        callbacks: {
          onEntryBatchCreated: this._onEntryBatchCreated,
          onEntryBatchFailed: this._onEntryBatchFailed,
          onEntryBatchUploaded: this._onEntryBatchUploaded,
          onItemFailed: this._onItemFailed,
          onItemUploaded: this._onItemUploaded,
          onItemQueued: this._onItemQueued
        },
        cancelToken: this._cancelSource.token
      }));

      if (this._cancelSource.token.isCanceled()) {
        return;
      }

      this.setState({
        isComplete: true
      });
    } catch (e) {
      if (this._cancelSource.token.isCanceled()) {
        return;
      }

      if (isCancel(e)) {
        this.setState({
          isCanceled: true
        });
      } else {
        this.setState({
          error: e
        });
      }
    }
  }

  private _onItemQueued = (props: IOnItemQueuedProps): void => {
    const {
      onItemQueued
    } = this.props;

    if (onItemQueued) {
      onItemQueued(props);
    }
  }

  private _onItemUploaded = (props: IOnItemUploadedProps): void => {
    const {
      onItemUploaded
    } = this.props;

    if (onItemUploaded) {
      onItemUploaded(props);
    }
  }

  private _onItemFailed = (props: IOnItemFailedProps): void => {
    const {
      onItemFailed
    } = this.props;

    if (onItemFailed) {
      onItemFailed(props);
    }
  }

  private _onEntryBatchCreated = (props: IOnEntryBatchCreatedProps): void => {
    const {
      onUploadStarted
    } = this.props;

    if (onUploadStarted) {
      onUploadStarted(props);
    }
  }

  private _onEntryBatchUploaded = (props: IOnEntryBatchUploadedProps): void => {
    const {
      onUploadCompleted
    } = this.props;

    if (onUploadCompleted) {
      onUploadCompleted(props);
    }
  }

  private _onEntryBatchFailed = (props: IOnEntryBatchFailedProps): void => {
    const {
      onUploadFailed
    } = this.props;

    if (onUploadFailed) {
      onUploadFailed(props);
    }
  }
}

/**
 * @public
 */
export type IUploaderCoreProps = IDecoratedProps<IBaseUploaderCoreProps, IItemCacheDispatchProp, {}>;

/**
 * @public
 */
export const UploaderCore: React.ComponentType<IUploaderCoreProps> = connectToItemCache()(BaseUploaderCore);
